import { SolicitacaoAgendamento, StatusSolicitacao, Pedido } from '../types';
import { SolicitacoesService } from './solicitacoesService';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings?: string[];
}

export class ValidationService {
  // Validação de dia fechado
  static async validarDiaFechado(
    data: string, 
    filial: string, 
    tipoProduto: string
  ): Promise<ValidationResult> {
    const errors: string[] = [];
    
    try {
      // 1. Verificar se o dia está fechado COMPLETO (bloqueia qualquer agendamento)
      const fechadoCompleto = await SolicitacoesService.verificarDiaFechado(data, filial, 'completo');
      
      if (fechadoCompleto) {
        errors.push(`O dia ${new Date(data).toLocaleDateString('pt-BR')} está com FECHAMENTO COMPLETO nesta filial. Não é possível agendar para nenhum tipo de produto.`);
        errors.push(`Motivo: A filial determinou o fechamento total das operações para esta data, impedindo o recebimento de qualquer carga.`);
        errors.push(`O que fazer: Escolha outra data disponível no calendário (dias em verde) ou entre em contato com a filial para mais informações.`);
        return {
          isValid: false,
          errors
        };
      }

      // 2. Verificar se o dia está marcado como FERIADO (bloqueia qualquer agendamento)
      const fechadoFeriado = await SolicitacoesService.verificarDiaFechado(data, filial, 'feriado');
      
      if (fechadoFeriado) {
        errors.push(`O dia ${new Date(data).toLocaleDateString('pt-BR')} está marcado como FERIADO nesta filial. Não é possível agendar.`);
        errors.push(`Motivo: A data foi configurada como feriado, dia sem expediente para recebimento de cargas.`);
        errors.push(`O que fazer: Escolha outro dia útil disponível no calendário (dias em verde) para realizar seu agendamento.`);
        return {
          isValid: false,
          errors
        };
      }
      
      // 3. Verificar fechamento específico por tipo de produto
      if (tipoProduto === 'SECOS') {
        const fechadoSecos = await SolicitacoesService.verificarDiaFechado(data, filial, 'secos');
        
        if (fechadoSecos) {
          errors.push(`O dia ${new Date(data).toLocaleDateString('pt-BR')} está fechado para SECOS nesta filial. Não é possível agendar para este tipo de produto.`);
          errors.push(`Motivo: O recebimento de produtos SECOS foi suspenso para esta data pela filial.`);
          errors.push(`O que fazer: Tente agendar para outra data disponível no calendário (dias em verde) ou escolha um tipo de produto diferente (Refrigerados ou Congelados) caso estejam disponíveis.`);
        }
      } else if (tipoProduto === 'REFRIGERADOS') {
        const fechadoRefrigerados = await SolicitacoesService.verificarDiaFechado(data, filial, 'refrigerados');
        
        if (fechadoRefrigerados) {
          errors.push(`O dia ${new Date(data).toLocaleDateString('pt-BR')} está fechado para REFRIGERADOS nesta filial. Não é possível agendar para este tipo de produto.`);
          errors.push(`Motivo: O recebimento de produtos REFRIGERADOS foi suspenso para esta data pela filial.`);
          errors.push(`O que fazer: Tente agendar para outra data disponível no calendário (dias em verde) ou escolha um tipo de produto diferente (Secos ou Congelados) caso estejam disponíveis.`);
        }
      } else if (tipoProduto === 'CONGELADOS') {
        const fechadoCongelados = await SolicitacoesService.verificarDiaFechado(data, filial, 'congelados');
        
        if (fechadoCongelados) {
          errors.push(`O dia ${new Date(data).toLocaleDateString('pt-BR')} está fechado para CONGELADOS nesta filial. Não é possível agendar para este tipo de produto.`);
          errors.push(`Motivo: O recebimento de produtos CONGELADOS foi suspenso para esta data pela filial.`);
          errors.push(`O que fazer: Tente agendar para outra data disponível no calendário (dias em verde) ou escolha um tipo de produto diferente (Secos ou Refrigerados) caso estejam disponíveis.`);
        }
      }
      
      return {
        isValid: errors.length === 0,
        errors
      };
    } catch (error) {
      console.error('Erro ao validar dia fechado:', error);
      return {
        isValid: false,
        errors: ['Erro ao verificar disponibilidade do dia.', 'Motivo: Não foi possível validar a disponibilidade da data selecionada.', 'O que fazer: Tente novamente em alguns instantes. Se o problema persistir, entre em contato com o suporte.']
      };
    }
  }

  // Validações para aprovação de solicitação
  static validarAprovacao(solicitacao: SolicitacaoAgendamento): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Verificar status atual (só pode aprovar Pedido digitado para converter em carga)
    if (solicitacao.status !== StatusSolicitacao.PEDIDO_DIGITADO) {
      errors.push(`Só é possível aprovar e converter em carga solicitações com status Pedido digitado. Status atual: ${solicitacao.status}`);
    }

    // Verificar data desejada (não pode ser no passado)
    const dataDesejada = new Date(solicitacao.data_desejada);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    
    if (dataDesejada < hoje) {
      errors.push('Data desejada não pode ser anterior a hoje');
    }

    // Verificar se há data desejada
    if (!solicitacao.data_desejada) {
      errors.push('Data desejada é obrigatória');
    }

    // Verificar peso e volume
    const peso = Number(solicitacao.peso);
    if (!solicitacao.peso || peso <= 0) {
      errors.push('Peso deve ser maior que zero');
    }

    const volume = Number(solicitacao.volume);
    if (!solicitacao.volume || volume <= 0) {
      errors.push('Volume deve ser maior que zero');
    }

    // Verificar fornecedor
    if (!solicitacao.fornecedor || solicitacao.fornecedor.trim().length === 0) {
      errors.push('Fornecedor é obrigatório');
    }

    // Verificar tipo de produto
    if (!solicitacao.tipo_produto || solicitacao.tipo_produto.trim().length === 0) {
      errors.push('Tipo de produto é obrigatório');
    }

    // Verificar tipo de carga
    if (!solicitacao.tipo_carga || solicitacao.tipo_carga.trim().length === 0) {
      errors.push('Tipo de carga é obrigatório');
    }

    // Avisos (não bloqueiam aprovação)
    if (solicitacao.prioridade && solicitacao.prioridade >= 5) {
      warnings.push('Solicitação com prioridade CRÍTICA - requer atenção imediata');
    }

    const diasParaEntrega = Math.ceil((dataDesejada.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
    if (diasParaEntrega <= 2) {
      warnings.push(`Prazo curto: ${diasParaEntrega} dias para entrega`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings: warnings.length > 0 ? warnings : undefined
    };
  }

  // Validações para reprovação
  static validarReprovacao(solicitacao: SolicitacaoAgendamento, motivo: string): ValidationResult {
    const errors: string[] = [];

    // Verificar status atual
    if (solicitacao.status !== StatusSolicitacao.PENDENTE) {
      errors.push(`Só é possível reprovar solicitações com status PENDENTE. Status atual: ${solicitacao.status}`);
    }

    // Verificar motivo
    if (!motivo || motivo.trim().length === 0) {
      errors.push('Motivo da reprovação é obrigatório');
    }

    if (motivo.trim().length < 10) {
      errors.push('Motivo da reprovação deve ter pelo menos 10 caracteres');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Validações para reagendamento
  static validarReagendamento(
    solicitacao: SolicitacaoAgendamento, 
    novaData: string, 
    motivo: string
  ): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Verificar status atual
    if (solicitacao.status !== StatusSolicitacao.PENDENTE) {
      errors.push(`Só é possível reagendar solicitações com status PENDENTE. Status atual: ${solicitacao.status}`);
    }

    // Verificar nova data
    if (!novaData) {
      errors.push('Nova data é obrigatória');
    } else {
      const dataNova = new Date(novaData);
      const dataAtual = new Date(solicitacao.data_desejada);
      
      // Não permitir reagendar para data anterior à original
      if (dataNova < dataAtual) {
        errors.push('Nova data não pode ser anterior à data original');
      }

      // Não permitir reagendar para muito distante no futuro
      const limiteMaximo = new Date();
      limiteMaximo.setDate(limiteMaximo.getDate() + 90);
      if (dataNova > limiteMaximo) {
        warnings.push('Nova data muito distante (mais de 90 dias)');
      }
    }

    // Verificar motivo
    if (!motivo || motivo.trim().length === 0) {
      errors.push('Motivo do reagendamento é obrigatório');
    }

    if (motivo.trim().length < 10) {
      errors.push('Motivo do reagendamento deve ter pelo menos 10 caracteres');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings: warnings.length > 0 ? warnings : undefined
    };
  }

  // Validações para solicitar pedido
  static validarSolicitacaoPedido(solicitacao: SolicitacaoAgendamento): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Verificar status atual
    if (solicitacao.status !== StatusSolicitacao.PENDENTE && 
        solicitacao.status !== StatusSolicitacao.AGUARDANDO_PEDIDO) {
      errors.push(`Solicitação já está em processo ou finalizada. Status atual: ${solicitacao.status}`);
    }

    // Verificar se já tem pedido vinculado
    if (solicitacao.pedido_compra_id) {
      errors.push('Solicitação já possui um pedido vinculado');
    }

    // Verificar dados mínimos para pedido
    if (!solicitacao.fornecedor || solicitacao.fornecedor.trim().length === 0) {
      errors.push('Fornecedor é obrigatório para gerar pedido');
    }

    if (!solicitacao.data_desejada) {
      errors.push('Data desejada é obrigatória para gerar pedido');
    }

    // Avisos
    const dataDesejada = new Date(solicitacao.data_desejada);
    const hoje = new Date();
    const diasParaEntrega = Math.ceil((dataDesejada.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diasParaEntrega <= 3) {
      warnings.push(`Prazo apertado: ${diasParaEntrega} dias para entrega`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings: warnings.length > 0 ? warnings : undefined
    };
  }

  // Validações para confirmação de pedido digitado
  static validarConfirmacaoPedido(
    pedido: Pedido,
    numeroPedido: string,
    arquivo?: File
  ): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Verificar número do pedido
    if (!numeroPedido || numeroPedido.trim().length === 0) {
      errors.push('Número do pedido é obrigatório');
    }

    if (numeroPedido.trim().length < 3) {
      errors.push('Número do pedido deve ter pelo menos 3 caracteres');
    }

    // Verificar se já tem número
    if (pedido.numero_pedido && pedido.numero_pedido.trim().length > 0) {
      errors.push('Pedido já possui número cadastrado');
    }

    // Verificar status do pedido
    if (pedido.status !== 'Aguardando pedido') {
      errors.push(`Pedido não está aguardando digitação. Status atual: ${pedido.status}`);
    }

    // Verificar arquivo (opcional mas recomendado)
    if (!arquivo) {
      warnings.push('Nenhum documento anexado ao pedido');
    } else if (arquivo.size > 10 * 1024 * 1024) { // 10MB
      errors.push('Arquivo muito grande (máximo 10MB)');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings: warnings.length > 0 ? warnings : undefined
    };
  }

  // Validações para aprovação final do pedido
  static validarAprovacaoFinal(pedido: Pedido): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Verificar status atual
    if (pedido.status !== 'Pedido digitado') {
      errors.push(`Só é possível aprovar pedidos digitados. Status atual: ${pedido.status}`);
    }

    // Verificar se tem número do pedido
    if (!pedido.numero_pedido || pedido.numero_pedido.trim().length === 0) {
      errors.push('Pedido deve ter número cadastrado');
    }

    // Verificar se tem solicitação vinculada
    if (!pedido.solicitacao_id) {
      warnings.push('Pedido não está vinculado a nenhuma solicitação');
    }

    // Verificar data de criação (não muito antiga)
    if (pedido.criado_em) {
      const dataCriacao = new Date(pedido.criado_em);
      const trintaDiasAtras = new Date();
      trintaDiasAtras.setDate(trintaDiasAtras.getDate() - 30);
      
      if (dataCriacao < trintaDiasAtras) {
        warnings.push('Pedido criado há mais de 30 dias');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings: warnings.length > 0 ? warnings : undefined
    };
  }

  // Validação geral de dados da solicitação
  static validarSolicitacao(solicitacao: SolicitacaoAgendamento): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Campos obrigatórios básicos
    const camposObrigatorios = [
      { campo: 'fornecedor', nome: 'Fornecedor' },
      { campo: 'data_desejada', nome: 'Data desejada' },
      { campo: 'peso', nome: 'Peso' },
      { campo: 'volume', nome: 'Volume' },
      { campo: 'tipo_produto', nome: 'Tipo de produto' },
      { campo: 'tipo_carga', nome: 'Tipo de carga' },
      { campo: 'solicitante_nome', nome: 'Nome do solicitante' },
      { campo: 'filial', nome: 'Filial' }
    ];

    for (const { campo, nome } of camposObrigatorios) {
      if (!solicitacao[campo as keyof SolicitacaoAgendamento] || 
          (typeof solicitacao[campo as keyof SolicitacaoAgendamento] === 'string' && 
           solicitacao[campo as keyof SolicitacaoAgendamento]?.toString().trim().length === 0)) {
        errors.push(`${nome} é obrigatório`);
      }
    }

    // Validações específicas
    const peso = Number(solicitacao.peso);
    if (solicitacao.peso && peso <= 0) {
      errors.push('Peso deve ser maior que zero');
    }

    const volume = Number(solicitacao.volume);
    if (solicitacao.volume && volume <= 0) {
      errors.push('Volume deve ser maior que zero');
    }

    // Validações de data
    if (solicitacao.data_desejada) {
      const dataDesejada = new Date(solicitacao.data_desejada);
      const hoje = new Date();
      
      if (dataDesejada < hoje) {
        errors.push('Data desejada não pode ser anterior a hoje');
      }
    }

    // Validações de formato
    const pesoFormato = Number(solicitacao.peso);
    if (solicitacao.peso && pesoFormato > 100000) {
      warnings.push('Peso muito elevado (mais de 100 toneladas)');
    }

    const volumeFormato = Number(solicitacao.volume);
    if (solicitacao.volume && volumeFormato > 1000) {
      warnings.push('Volume muito elevado (mais de 1000 m³)');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings: warnings.length > 0 ? warnings : undefined
    };
  }

  // Método auxiliar para formatar mensagens de erro
  static formatarMensagens(validationResult: ValidationResult): string {
    let mensagem = '';
    
    if (validationResult.errors.length > 0) {
      mensagem += 'Erros:\n' + validationResult.errors.map(e => `  - ${e}`).join('\n');
    }
    
    if (validationResult.warnings && validationResult.warnings.length > 0) {
      if (mensagem) mensagem += '\n\n';
      mensagem += 'Avisos:\n' + validationResult.warnings.map(w => `  - ${w}`).join('\n');
    }
    
    return mensagem;
  }
}
